import React from 'react'

import { SimpleTable, TableSkeleton, Alert } from '@/components/ui'
import { useApi, useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { validateDateRangeWithin30Days } from '@/insights/common/formatUtils'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { NonAlarmDateOptionsWithCustom } from '@/insights/components/CustomSingleSelect'
import {
  ApiService,
  type Asset,
  AssetTypeFilter,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { downloadCsv } from '@/utils/csvExport'
import { formatUserDate } from '@/utils/formatDate'

interface FuelHistoryParams extends InsightParametersType {
  companyId?: number
  assetId?: string
  date?: string
  from?: string
  to?: string
}

interface FuelHistory {
  id: number
  asset: {
    id: number
    name: string
  }
  deviceName: string
  sampleTime: Date
  fuelPercent: number | null
}

const toRequestParams = ({ assetId, ...rest }: FuelHistoryParams): InsightParametersType => ({
  ...rest,
  assetIds: assetId,
  date: rest.date || 'today',
})

const FuelHistory: InsightDefinition<FuelHistoryParams, FuelHistory[]> = {
  active: true,
  slug: 'fuel/fuel-history',
  category: 'fuel',
  name: 'Fuel History',
  description: 'Display fuel level history for an asset for a given date range',
  defaultWidth: 'medium',
  defaultAspectRatio: 16 / 9,

  configuration: {
    transform: ({ parameters, field }: { parameters: FuelHistoryParams; field: keyof FuelHistoryParams }) => {
      if (field === 'date' && parameters.date !== 'custom') {
        return { ...parameters, from: undefined, to: undefined }
      }
      return parameters
    },

    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      { id: 'assetId', control: 'AssetDropdownSingle', required: true, assetType: AssetTypeFilter.InTransit },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: NonAlarmDateOptionsWithCustom,
      },
      {
        id: 'customDate',
        label: 'Date',
        control: 'DateTimeRange',
        dependsOn: 'date',
        dependsOnValue: 'custom',
      },
    ],

    isValid: (configuration: FuelHistoryParams) => {
      if (!configuration?.companyId || !configuration.assetId) {
        return 'Company and Asset are required.'
      }

      if (configuration.date === 'custom') {
        if (!configuration.from || !configuration.to) {
          return 'Enter the custom date range'
        }
        const fromDate = new Date(configuration.from)
        const toDate = new Date(configuration.to)
        if (fromDate > toDate) {
          return 'Start date must be before end date'
        }
        const dateValidation = validateDateRangeWithin30Days(fromDate.toISOString(), toDate.toISOString())
        if (dateValidation !== true) {
          return dateValidation
        }
      }

      return true
    },
  },

  load: async (api: ApiService, parameters: FuelHistoryParams) => {
    const query = buildQuery(toRequestParams(parameters))
    const path = `/insights/fuel/reefer-fuel-by-asset?${query}&skip=0&take=1000`
    const response = await api.get<FuelHistory[]>({
      path,
      origin: 'FuelHistory',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data?.length) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: FuelHistoryParams, title: string) => {
    const path = '/insights/fuel/reefer-fuel-by-asset'
    await downloadCsv(api, path, { ...toRequestParams(parameters), fuelHistory: 'true' }, title)
  },

  Display: ({ status, data, configuration }) => {
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      return <TableSkeleton columns={4} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      return null
    }

    const headers = ['Asset Name', 'Device Name', 'Date', 'Fuel Level %']
    const body = data
      .slice()
      .sort((a, b) => new Date(b.sampleTime).getTime() - new Date(a.sampleTime).getTime())
      .map(
        (sample): Record<string, string> => ({
          'Asset Name': sample.asset?.name ?? '-',
          'Device Name': sample.deviceName ?? '-',
          Date: formatUserDate(sample.sampleTime),
          'Fuel Level %': sample.fuelPercent != null ? String(sample.fuelPercent) : '-',
        })
      )

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} cellClassNames={{ Date: 'text-nowrap' }} />
        </div>
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const [asset, setAsset] = React.useState<Asset>()
    const { companies } = useCompany()
    const { api } = useApi()
    const params = configuration.parameters as FuelHistoryParams

    React.useEffect(() => {
      if (!api) {
        return
      }
      const fn = async () => {
        if (params.assetId) {
          const response = await api.get<Asset[]>({
            path: `/fresh/assets?companyId=${params.companyId}&id=${params.assetId}&deleted=false`,
            origin: 'FuelHistory#caption',
          })
          if (response.data?.length) {
            setAsset(response.data[0])
          } else {
            setAsset({ id: Number(params.assetId) } as Asset)
          }
        }
      }
      fn()
    }, [params])

    if (status.code === InsightStatusCode.ERROR) {
      return ''
    }
    const fields: Record<string, string> = {}
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (asset) {
      fields['Asset'] = asset.name
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || (status.code === InsightStatusCode.ERROR && data === undefined)) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default FuelHistory
